<template>
  <hr-date-field
    ref="dateField"
    :model-value="calendarDate"
    :label="label"
    :dense="dense"
    :disable="disable"
    :readonly="readonly"
    :clearable="clearable"
    :options="options"
    :rules="rules"
    lazy-rules
    :legacy-props="{ outlined }"
    :time-zone="currentIANATimezone"
    @update:model-value="onChangeDate"
  >
    <template
      v-if="appendImage"
      #append
    >
      <q-avatar size="24px">
        <img :src="appendImage">
      </q-avatar>
    </template>
  </hr-date-field>
</template>
<script>
import { Timestamp } from 'firebase/firestore';
import { HrDateField } from 'src/design-system';
import { useTime } from 'src/composables/time';

export default {
  name: 'DateInput',

  components: {
    HrDateField,
  },

  props: {
    modelValue: {
      type: [Timestamp, null],
      required: true,
    },
    label: {
      type: String,
      default: null,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Function,
      default: () => (true),
    },
    preserveTime: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: null,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    appendImage: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup() {
    const { getLocaleDateString, extractDate, formatDate, currentIANATimezone } = useTime();
    return { getLocaleDateString, extractDate, formatDate, currentIANATimezone };
  },

  computed: {

    rules() {
      if (this.errorMessage === null) {
        return;
      }
      return [() => (!!this.calendarDate || this.errorMessage)];
    },

    // HrDateField's model is a 'yyyy-MM-dd' calendar string; this component's is a Timestamp, which
    // its callers store directly.
    calendarDate() {
      if (!(this.modelValue instanceof Timestamp)) {
        return null;
      }
      return this.formatDate(this.modelValue.toDate(), 'yyyy-MM-dd');
    }, // calendarDate()
  },

  methods: {
    onChangeDate(newCalendarDate) {
      if (newCalendarDate === null) {
        this.$emit('update:modelValue', null);
        return;
      }

      if (this.preserveTime && (this.modelValue instanceof Timestamp)) {
        const timeOfDay = this.formatDate(this.modelValue.toDate(), 'HH:mm');
        this.$emit('update:modelValue', Timestamp.fromDate(
          this.extractDate(`${newCalendarDate} ${timeOfDay}`, 'yyyy-MM-dd HH:mm'),
        ));
        return;
      }

      this.$emit('update:modelValue', Timestamp.fromDate(
        this.extractDate(newCalendarDate, 'yyyy-MM-dd'),
      ));
    }, // onChangeDate()

    /**
     * Re-runs the field's rules, for a rule that compares against another field.
     *
     * Delegated rather than reimplemented: `HrDateField` revalidates itself whenever the calendar sets
     * a value, and this is the same call for the case it cannot see — the authorization editor's end
     * date has to be re-checked when its start date moves, and that field's own value did not change.
     */
    validate() {
      return this.$refs.dateField?.validate() ?? true;
    }, // validate()
  },

};
</script>
